import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { uniq } from 'lodash';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { deserialize } from 'santee-dcts';
import { Settings } from '../../core/settings';
import { Accession } from '../shared/accession';
import { AccessionsSearcher } from '../shared/accessions-storage';
import { AccessionSearchFieldReadModel, EmailSearchFieldReadModel, EQueryIdSearchFieldReadModel } from './models/e-query-escalation-search-field';

const enum EscalationSearchFieldType {
    Email = 0,
    EQueryId = 1,
    AccessionNumber = 2,
    AccessionNumberHide = 3,
}

@Injectable()
export class AdminEQueryEscalationSearchFieldsStorage implements AccessionsSearcher {
    constructor(
        private readonly http: HttpClient,
        private readonly settings: Settings,
    ) {}

    public loadEmails(term: string): Observable<string[]> {
        return this.searchByType(EscalationSearchFieldType.Email, term, res => deserialize(res, EmailSearchFieldReadModel).searchFields, x => x.email);
    }

    public loadEQueries(term: string): Observable<number[]> {
        return this.searchByType(EscalationSearchFieldType.EQueryId, term, res => deserialize(res, EQueryIdSearchFieldReadModel).searchFields, x => x.eQueryId);
    }

    public loadAccessionNumber(term: string): Observable<string[]> {
        return this.searchByType(EscalationSearchFieldType.AccessionNumber, term, res => deserialize(res, AccessionSearchFieldReadModel).searchFields, x => x.accessionNumber);
    }

    public loadAccessionNumberHideEQuery(term: string): Observable<string[]> {
        return this.searchByType(EscalationSearchFieldType.AccessionNumberHide, term, res => deserialize(res, AccessionSearchFieldReadModel).searchFields, x => x.accessionNumber);
    }

    public search(_: number, searchTerm: string): Observable<Accession[]> {
        return this.searchByType(
            EscalationSearchFieldType.AccessionNumber,
            searchTerm,
            res => deserialize(res, AccessionSearchFieldReadModel).searchFields,
            x => {
                const accession = new Accession();
                accession.accessionId = x.accessionId;
                accession.accessionNumber = x.accessionNumber;
                accession.projectNumber = x.projectNumber;
                return accession;
            });
    }

    private searchByType<T, TResult>(
        fieldType: EscalationSearchFieldType,
        term: string,
        deserializerFn: (res: Object) => T[],
        fieldFn: (x: T) => TResult,
    ): Observable<TResult[]> {
        return this.http
            .get(`${this.settings.apiUrl}/admin/e-query-escalation-search-fields/${fieldType}`, { params: { 'searchTerm': term } })
            .pipe(
                map(res => {
                    const searchFields = deserializerFn(res);
                    return uniq(searchFields.map(fieldFn));
                })
            );
    }
}